import { useTranslation } from 'react-i18next';
import { MainLayout } from '../../Layout/MainLayout';
import {
  Box,
  BrightyStepButton,
  Button,
  Grid,
  HookForm,
  Stack,
  Step,
  Stepper,
} from '@razz-team/ui-web';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Route } from '../../../routes/account.payouts_.$payoutId_.create';
import { useAccountsWithAccountQuery } from '../../../api/accounts';
import { useIdempotencyKey } from '../../../hooks/useIdempotencyKey';
import { mapClientMoneyToServerMoney, parseFloatValue } from '@razz-team/utils';
import { TransferFormValues, mapTransferBeneficiaryToCreateApi } from './types';
import {
  useCreatePayoutExternalTransferMutation,
  useCreatePayoutInternalTransferMutation,
  usePayoutQuery,
} from '../../../api/payouts';
import { TransferBeneficiaryStep } from '../TransferPage/TransferBeneficiaryStep';
import { TransferAmountStep } from '../TransferPage/TransferAmountStep';
import { MainBreadcrumbs } from '../../Layout/MainBreadcrumbs';
import { calculateDefaultAccountId } from '../TransferPage/calculateDefaultAccountId';

enum PayoutTransferSteps {
  beneficiary,
  amount,
  confirm,
}

export function PayoutAddTransferPage() {
  const { t } = useTranslation();
  const [step, setStep] = useState<PayoutTransferSteps>(PayoutTransferSteps.beneficiary);

  const { createdAt, accountId: searchAccountId } = Route.useSearch();
  const { payoutId } = Route.useParams();
  const navigate = Route.useNavigate();

  const payoutQuery = usePayoutQuery({ createdAt, payoutId: payoutId });

  const form = useForm<TransferFormValues>({});
  const beneficiary = form.watch('beneficiary');

  const accountsQuery = useAccountsWithAccountQuery(searchAccountId);
  const accounts = useMemo(() => accountsQuery.data ?? [], [accountsQuery.data]);

  const updateDefaultAccountId = useCallback(() => {
    const values = form.getValues();
    form.setValue(
      'accountId',
      calculateDefaultAccountId({
        accounts,
        currency: values.beneficiary.value.currency,
        defaultAccountId: searchAccountId,
      }),
    );
  }, [accounts, form, searchAccountId]);

  const goToBeneficiary = useCallback(() => setStep(PayoutTransferSteps.beneficiary), []);
  const goToAmount = useCallback(() => {
    updateDefaultAccountId();
    setStep(PayoutTransferSteps.amount);
  }, [updateDefaultAccountId]);

  const idempotencyKey = useIdempotencyKey();
  const createInternalTransfer = useCreatePayoutInternalTransferMutation({
    payoutId,
    createdAt,
    idempotencyKey,
  });
  const createExternalTransfer = useCreatePayoutExternalTransferMutation({
    payoutId,
    createdAt,
    idempotencyKey,
  });

  const handleSubmit = useCallback(
    (values: TransferFormValues) => {
      switch (step) {
        case PayoutTransferSteps.beneficiary:
          updateDefaultAccountId();
          setStep(PayoutTransferSteps.amount);
          break;
        case PayoutTransferSteps.amount:
          const amount = mapClientMoneyToServerMoney({
            amount: parseFloatValue(values.clientAmount),
            currency: values.beneficiary.value.currency,
          });

          if (values.beneficiary.type === 'internal') {
            createInternalTransfer.mutate({
              amount,
              comment: values.comment,
              receiverId: values.beneficiary.value.receiverId,
              sourceAccountId: values.accountId,
            });
          } else {
            createExternalTransfer.mutate({
              amount,
              evidence: values.evidence,
              reference: values.reference,
              sourceAccountId: values.accountId,
              ...mapTransferBeneficiaryToCreateApi(values.beneficiary),
            });
          }

          break;
      }
    },
    [step, createExternalTransfer, createInternalTransfer, updateDefaultAccountId],
  );

  useEffect(() => {
    if (
      // wait for payout query to finish
      !payoutQuery.isFetching &&
      (createInternalTransfer.isSuccess || createExternalTransfer.isSuccess)
    ) {
      navigate({
        to: '/account/payouts/$payoutId',
        params: { payoutId },
        search: { createdAt },
      });
    }
  }, [
    navigate,
    payoutId,
    createdAt,
    payoutQuery.isFetching,
    createExternalTransfer.isSuccess,
    createInternalTransfer.isSuccess,
  ]);

  let isSubmitVisible = true;
  if (step === PayoutTransferSteps.beneficiary && !beneficiary?.type) {
    isSubmitVisible = false;
  }

  return (
    <MainLayout
      title={
        <MainBreadcrumbs
          title={t('Add payment')}
          parent={{
            title: payoutQuery.data?.payout.name,
            loading: payoutQuery.isPending,
            to: '/account/payouts/$payoutId',
            params: { payoutId },
            search: { createdAt },
          }}
        />
      }
      compact
    >
      <Grid size={12}>
        <Box sx={{ width: '100%' }}>
          <Stepper alternativeLabel activeStep={step} nonLinear>
            <Step
              active={step === PayoutTransferSteps.beneficiary}
              disabled={step === PayoutTransferSteps.confirm}
            >
              <BrightyStepButton onClick={goToBeneficiary}>{t('Recipient')}</BrightyStepButton>
            </Step>
            <Step active={step === PayoutTransferSteps.amount}>
              <BrightyStepButton>{t('Amount')}</BrightyStepButton>
            </Step>
            <Step active={step === PayoutTransferSteps.confirm}>
              <BrightyStepButton>{t('Confirmation')}</BrightyStepButton>
            </Step>
          </Stepper>
        </Box>
      </Grid>
      <Grid
        component={HookForm}
        container
        display="flex"
        justifyContent="center"
        form={form}
        onSubmit={handleSubmit}
        size={12}
      >
        <Stack direction="column" width="100%" maxWidth={500} spacing={6} alignItems="stretch">
          {step === PayoutTransferSteps.beneficiary ? (
            <TransferBeneficiaryStep form={form} onFinish={goToAmount} internalEnabled />
          ) : null}
          {step === PayoutTransferSteps.amount ? (
            <TransferAmountStep form={form} accounts={accounts} />
          ) : null}
          {isSubmitVisible ? (
            <Button
              type="submit"
              size="large"
              color="primary"
              loading={
                payoutQuery.isFetching ||
                createInternalTransfer.isPending ||
                createExternalTransfer.isPending
              }
              fullWidth
            >
              {step === PayoutTransferSteps.amount ? t('Add') : t('Continue')}
            </Button>
          ) : null}
        </Stack>
      </Grid>
    </MainLayout>
  );
}
